import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import LogoSection from '../components/LogoSection';
import { startListeningToCompletedPayments, loadMoreCompletedPayments } from '../redux/actions';

const ROWS_PER_PAGE = 20;

const CompletedTransactions = () => {
    const dispatch = useDispatch();
    const completedPayments = useSelector((state) => state.completedPayments);
    const isLoading = useSelector((state) => state.completedPaymentsLoading);
    const tenantId = useSelector((state) => state.userTenant);
    const hasMore = useSelector((state) => state.completedPaymentsHasMore);
    const [unsubscribe, setUnsubscribe] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);

    useEffect(() => {
        if (tenantId) {
            const unsub = dispatch(startListeningToCompletedPayments(tenantId));
            setUnsubscribe(() => unsub);
        }

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [tenantId, dispatch]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'No date';
        const d = timestamp instanceof Date ? timestamp : timestamp.toDate();
        return d.toLocaleString();
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return completedPayments;
        const q = search.toLowerCase();
        return completedPayments.filter((p) => {
            const email = (p.email || '').toLowerCase();
            const amount = (p.amount / 100).toFixed(2);
            const date = formatDate(p.timestamp).toLowerCase();
            return email.includes(q) || amount.includes(q) || date.includes(q);
        });
    }, [completedPayments, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const paginated = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

    // Reset to first page when search changes
    useEffect(() => {
        setPage(0);
    }, [search]);

    const handleLoadMore = async () => {
        setLoadingMore(true);
        await dispatch(loadMoreCompletedPayments(tenantId));
        setLoadingMore(false);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex justify-center">
                <div className="w-full max-w-screen-lg flex flex-col items-center space-y-3 px-4 md:px-16">
                    <LogoSection />
                    <div className="w-full text-center">
                        <div className="animate-pulse">Loading transactions...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex justify-center">
            <div className="w-full max-w-screen-lg flex flex-col items-center space-y-3 px-4 md:px-16">
                <LogoSection />
                <h1 className="text-2xl font-bold mb-2">Completed Transactions</h1>
                <p className="text-sm text-gray-500 mb-2">{completedPayments.length} transactions loaded</p>

                {/* Search */}
                <div className="w-full">
                    <input
                        type="text"
                        placeholder="Search by email, amount, or date..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Table */}
                <div className="w-full overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="px-4 py-3 font-semibold">#</th>
                                <th className="px-4 py-3 font-semibold">Email</th>
                                <th className="px-4 py-3 font-semibold">Amount</th>
                                <th className="px-4 py-3 font-semibold">Target</th>
                                <th className="px-4 py-3 font-semibold">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.map((payment, idx) => (
                                <tr key={payment.id} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-3 text-gray-500">{page * ROWS_PER_PAGE + idx + 1}</td>
                                    <td className="px-4 py-3">{payment.email || '—'}</td>
                                    <td className="px-4 py-3 font-medium">${(payment.amount / 100).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-gray-600">{payment.target || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500">{formatDate(payment.timestamp)}</td>
                                </tr>
                            ))}
                            {paginated.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                        {search ? 'No transactions match your search' : 'No completed transactions found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="w-full flex items-center justify-between py-3">
                        <p className="text-sm text-gray-500">
                            Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 0}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-600">
                                Page {page + 1} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages - 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {/* Load More */}
                {hasMore && (
                    <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-100 disabled:opacity-50 mb-6"
                    >
                        {loadingMore ? 'Loading...' : 'Load More Transactions'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default CompletedTransactions;
